import React, {
  useEffect,
  useRef,
  useContext,
  useState,
  useCallback,
} from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useSelector, shallowEqual } from "react-redux";
import classnames from "classnames";
import {
  makeSpotlightPeersSelector,
  videoBoxesSelector,
} from "../../helpers/Selectors";
import RoomContext from "../../context/RoomContext";
import Me from "../Presence/Me";
import Peer from "../Presence/Peer";
import SpeakerPeer from "../Presence/SpeakerPeer";
import Grid from "@material-ui/core/Grid";
import debounce from "../../utils/debounce";

const RATIO = 1.334;
const PADDING_V = 50;
const PADDING_H = 0;
const FILMSTRING_PADDING_V = 50;
const FILMSTRING_PADDING_H = 0;

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      borderRadius: 24,
      height: "100%",
      width: "100%",
      display: "grid",
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr 0.25fr",
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
        width: "100%",
      },
      paddingBottom: 80,
    },
    speaker: {
      borderRadius: 24,
      gridArea: "1 / 1 / 1 / 1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
        flexDirection: "column",
      },
    },
    filmStrip: {
      gridArea: "2 / 1 / 2 / 1",
    },
    filmItem: {
      display: "flex",
      borderRadius: 24,
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
      },
      "&.selected": {
        borderColor: "rgba(55, 126, 255, 1)",
      },
      "&.active": {
        opacity: "0.6",
      },
    },
    hiddenToolBar: {
      paddingTop: 0,
      transition: "padding .5s",
    },
    showingToolBar: {
      transition: "padding .5s",
    },
  })
);

const FilmStrip = () => {
  const classes = useStyles();
  const { roomClient } = useContext(RoomContext);
  const rootRef = useRef();
  const activePeerRef = useRef();
  const filmStripRef = useRef();

  const [lastSpeaker, setLastSpeaker] = useState();
  const [speakerWidth, setSpeakerWidth] = useState();
  const [speakerHeight, setSpeakerHeight] = useState();
  const [filmStripWidth, setFilmStripWidth] = useState();
  const [filmStripHeight, setFilmStripHeight] = useState();

  const activeSpeakerId = useSelector((state) => state.room.activeSpeakerId);
  const selectedPeerId = useSelector((state) => state.room.selectedPeerId);
  const peers = useSelector((state) => state.peers);
  const room = useSelector((state) => state.room);
  const consumers = useSelector((state) => state.consumers);
  const myId = useSelector((state) => state.me.id);
  const spotlights = useSelector(
    (state) => makeSpotlightPeersSelector(state)(state),
    shallowEqual
  );
  const boxes = useSelector((state) => videoBoxesSelector(state));
  const toolbarsVisible = useSelector((state) => state.room.toolbarsVisible);
  const permanentTopBar = useSelector(
    (state) => state.settings.permanentTopBar
  );
  const hasWebinarPresenterPermission = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_WEBINAR.includes(role)
    )
  );
  // Find the name of the peer which is currently speaking. This is either
  // the latest active speaker, or the manually selected peer, or, if no
  // person has spoken yet, the first peer in the list of peers.
  const getActivePeerId = useCallback(() => {
    if (selectedPeerId && peers[selectedPeerId]) {
      return selectedPeerId;
    }

    if (lastSpeaker && peers[lastSpeaker]) {
      return lastSpeaker;
    }

    const peerIds = Object.keys(peers);

    if (peerIds.length > 0) {
      return peerIds[0];
    }
  }, [selectedPeerId, peers, lastSpeaker]);
  const activePeerId = getActivePeerId();

  const updateDimensions = useCallback(() => {
    const isSharingCamera = (peerId) =>
      peers[peerId] &&
      peers[peerId].consumers.some(
        (consumer) => consumers[consumer].source === "screen"
      );
    const root = rootRef.current;

    const availableWidth = root ? root.clientWidth : 0;
    // Grid is:
    // 4/5 speaker
    // 1/5 filmstrip
    const availableSpeakerHeight =
      root.clientHeight * 0.8 -
      (toolbarsVisible || permanentTopBar ? PADDING_V : PADDING_H);

    const availableFilmstripHeight = root.clientHeight * 0.2;

    const speaker = activePeerRef.current;

    if (speaker) {
      let speakerWidth = availableWidth - PADDING_H;

      let speakerHeight = speakerWidth / RATIO - 60;

      /*      if (isSharingCamera(getActivePeerId())) {
        speakerWidth /= 2;
        speakerHeight = speakerWidth / RATIO - 60;
      }*/

      if (speakerHeight > availableSpeakerHeight - PADDING_V) {
        speakerHeight = availableSpeakerHeight - PADDING_V;
        speakerWidth = speakerHeight * RATIO;
      }
      setSpeakerWidth(speakerWidth);
      setSpeakerHeight(speakerHeight);
    }

    const filmStrip = filmStripRef.current;

    if (filmStrip) {
      let filmStripHeight = availableFilmstripHeight - FILMSTRING_PADDING_V;

      let filmStripWidth = filmStripHeight * RATIO;

      if (filmStripWidth * boxes > availableWidth - FILMSTRING_PADDING_H) {
        filmStripWidth = (availableWidth - FILMSTRING_PADDING_H) / boxes;
        filmStripHeight = filmStripWidth / RATIO;
      }
      setFilmStripWidth(filmStripWidth);
      setFilmStripHeight(filmStripHeight);
    }
  }, [
    boxes,
    permanentTopBar,
    toolbarsVisible,
    consumers,
    peers,
    getActivePeerId,
  ]);

  useEffect(() => {
    const debouncedUpdate = debounce(updateDimensions, 250);
    // window.resize event listener
    window.addEventListener("resize", debouncedUpdate);

    updateDimensions();

    return () => {
      window.removeEventListener("resize", debouncedUpdate);
    };
  }, [updateDimensions]);

  useEffect(() => {
    if (activeSpeakerId != null && activeSpeakerId !== myId) {
      setLastSpeaker(activeSpeakerId);
    }
  }, [activeSpeakerId, myId]);

  return (
    <div
      className={classnames(
        classes.root,
        toolbarsVisible || permanentTopBar
          ? classes.showingToolBar
          : classes.hiddenToolBar
      )}
      ref={rootRef}
    >
      <div className={classes.speaker} ref={activePeerRef}>
        {peers[activePeerId] &&
          (!room.webinar || (room.webinar && spotlights.length !== 0)) && (
            <SpeakerPeer
              id={activePeerId}
              style={{
                width: speakerWidth,
                height: speakerHeight,
              }}
              onFilmStrip={false}
            />
          )}

        {room.webinar && spotlights.length === 0 && (
          <Me
            style={{
              width: speakerWidth,
              height: speakerHeight,
            }}
            onFilmStrip={false}
          />
        )}
      </div>

      <div className={classes.filmStrip} ref={filmStripRef}>
        <Grid container justify="center" spacing={0}>
          {(!room.webinar ||
            !hasWebinarPresenterPermission ||
            (room.webinar && spotlights.length !== 0)) && (
            <Grid item>
              <div
                className={classnames(classes.filmItem, {
                  active: myId === activePeerId,
                })}
              >
                <Me
                  style={{
                    width: filmStripWidth,
                    height: filmStripHeight,
                    backgroundSize: "contain",
                  }}
                  onFilmStrip={true}
                />
              </div>
            </Grid>
          )}

          {Object.keys(peers).map((peerId) => {
            if (
              spotlights.find(
                (spotlightsElement) => spotlightsElement === peerId
              )
            ) {
              return (
                <Grid key={peerId} item>
                  <div
                    key={peerId}
                    onClick={() => roomClient.setSelectedPeer(peerId)}
                    className={classnames(classes.filmItem, {
                      selected: selectedPeerId === peerId,
                      active: peerId === activePeerId,
                    })}
                  >
                    <Peer
                      id={peerId}
                      style={{
                        width: filmStripWidth,
                        height: filmStripHeight,
                        backgroundSize: "contain",
                      }}
                      onFilmStrip={true}
                    />
                  </div>
                </Grid>
              );
            } else {
              return "";
            }
          })}
        </Grid>
      </div>
    </div>
  );
};

export default FilmStrip;
